import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft } from "lucide-react";
import Navbar from "../components/Navbar";
import MobileMenu from "../components/MobileMenu";
import Footer from "../components/Footer";
import Lightbox from "../components/Lightbox";
import ShareButton from "../components/ShareButton";
import { useStory, photoUrl } from "../lib/usePortfolio";
import { useMeta } from "../lib/useMeta";

const EASE = [0.22, 1, 0.36, 1];

export default function Story() {
  const { slug } = useParams();
  const { data: story, isLoading, isError } = useStory(slug);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  useEffect(() => window.scrollTo(0, 0), [slug]);
  useMeta(story?.title, story?.excerpt || [story?.couple, story?.venue, story?.date].filter(Boolean).join(" · "));

  const frames = (story?.photos ?? []).map((p) => ({ ...p, url: photoUrl(p) }));
  const paragraphs = (story?.body ?? "").split(/\n\s*\n/).filter((p) => p.trim());

  return (
    <>
      <Navbar />
      <MobileMenu />
      <main data-testid="story-page" className="pt-[72px]">
        {isLoading ? (
          <p className="max-w-7xl mx-auto px-6 lg:px-10 py-40 text-[11px] font-mono tracking-[0.3em] uppercase text-stone-500 animate-pulse">Opening the story…</p>
        ) : isError || !story ? (
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-40" data-testid="story-not-found">
            <p className="font-serif text-3xl italic text-stone-400">This story could not be found.</p>
            <Link to="/journal" className="mt-8 inline-flex items-center gap-2 text-[11px] font-mono tracking-[0.2em] uppercase text-champagne"><ArrowLeft size={14} /> Back to the Journal</Link>
          </div>
        ) : (
          <>
            <section className="relative">
              {story.cover && (
                <motion.div initial={{ opacity: 0, scale: 1.04 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.6, ease: EASE }} className="relative h-[60vh] md:h-[75vh] overflow-hidden">
                  <img src={photoUrl(story.cover)} alt={story.title} className="h-full w-full object-cover" data-testid="story-cover" />
                  <div className="absolute inset-0 bg-gradient-to-t from-obsidian via-obsidian/30 to-obsidian/10" />
                </motion.div>
              )}
              <div className={`max-w-7xl mx-auto px-6 lg:px-10 ${story.cover ? "-mt-32 md:-mt-40 relative" : "pt-24"}`}>
                <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 1, delay: 0.3, ease: EASE }}>
                  <Link to="/journal" data-testid="story-back-link" className="inline-flex items-center gap-2 text-[10px] font-mono tracking-[0.25em] uppercase text-stone-400 hover:text-champagne transition-colors"><ArrowLeft size={12} /> The Journal</Link>
                  <p className="mt-6 text-[11px] font-mono tracking-[0.25em] uppercase text-champagne">
                    {[story.couple, story.venue, story.date].filter(Boolean).join(" · ")}
                  </p>
                  <h1 data-testid="story-title" className="mt-4 max-w-4xl font-serif text-4xl sm:text-5xl lg:text-6xl font-normal tracking-tight text-ivory leading-[1.05]">{story.title}</h1>
                  <div className="mt-8"><ShareButton slug={story.slug} title={story.title} excerpt={story.excerpt} /></div>
                </motion.div>
              </div>
            </section>

            <section className="max-w-7xl mx-auto px-6 lg:px-10 py-20 md:py-28 grid md:grid-cols-12 gap-12">
              <div className="md:col-span-3">
                {story.excerpt && <p className="font-serif text-xl italic text-champagne leading-relaxed">{story.excerpt}</p>}
              </div>
              <div className="md:col-span-8 md:col-start-5 space-y-6" data-testid="story-body">
                {paragraphs.map((p, i) => (
                  <p key={i} className={`font-light leading-[1.85] text-stone-300 ${i === 0 ? "text-lg first-letter:font-serif first-letter:text-5xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.8] first-letter:text-champagne" : "text-base"}`}>{p}</p>
                ))}
              </div>
            </section>

            {frames.length > 0 && (
              <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-32" data-testid="story-gallery">
                <p className="text-[11px] font-mono tracking-[0.25em] uppercase text-stone-500 mb-8">{frames.length} Frames</p>
                <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 space-y-5">
                  {frames.map((f, i) => (
                    <motion.figure
                      key={f.id}
                      initial={{ opacity: 0, y: 24 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true, margin: "-40px" }}
                      transition={{ duration: 0.7, ease: EASE }}
                      data-testid={`story-frame-${f.id}`}
                      onClick={() => setLightboxIndex(i)}
                      className="group relative break-inside-avoid overflow-hidden bg-surface cursor-pointer"
                    >
                      <img src={f.url} alt={f.title} loading="lazy" className="w-full object-cover transition-transform duration-[1200ms] group-hover:scale-105" />
                      <div className="absolute inset-0 bg-gradient-to-t from-obsidian/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <figcaption className="absolute bottom-0 left-0 right-0 p-4 translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all duration-500">
                        <p className="font-serif text-ivory">{f.title}</p>
                      </figcaption>
                    </motion.figure>
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
      <Footer />
      <AnimatePresence>
        {lightboxIndex !== null && (
          <Lightbox images={frames} index={lightboxIndex} onClose={() => setLightboxIndex(null)} onNavigate={setLightboxIndex} />
        )}
      </AnimatePresence>
    </>
  );
}
